import React from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Users, ArrowLeftRight, CheckCircle2, Clock, DollarSign, TrendingUp, Wallet, Award } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";
import api from "@/services/api";
import StatCard from "@/components/StatCard";
import { fmtMoney } from "@/utils/format";
import { useLang } from "@/context/LanguageContext";
import { Skeleton } from "@/components/ui/skeleton";

export default function AdminDashboard() {
  const { t } = useLang();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => (await api.get("/stats/admin")).data,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-heading font-bold">{t("dashboard")}</h1>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
        </div>
        <Skeleton className="h-80 rounded-2xl" />
      </div>
    );
  }

  const s = data || {};

  return (
    <div className="space-y-8">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">{t("dashboard")}</h1>
        <p data-testid="admin-area-sub" className="text-sm text-muted-foreground mt-1">{t("admin_area_sub")}</p>
      </motion.div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard label={t("total_agents")} value={s.total_agents ?? 0} icon={Users} accent="blue" testId="stat-agents" delay={0.0} compact />
        <StatCard label={t("total_transfers")} value={s.total_transfers ?? 0} icon={ArrowLeftRight} accent="gold" testId="stat-transfers" delay={0.05} compact />
        <StatCard label={t("delivered")} value={s.delivered ?? 0} icon={CheckCircle2} accent="green" testId="stat-delivered" delay={0.1} compact />
        <StatCard label={t("pending")} value={s.pending ?? 0} icon={Clock} accent="red" testId="stat-pending" delay={0.15} compact />
        <StatCard label={t("total_amount")} value={fmtMoney(s.total_amount ?? 0)} icon={DollarSign} accent="blue" testId="stat-amount" delay={0.2} compact />
        <StatCard label={t("system_profit")} value={fmtMoney(s.system_profit ?? 0)} icon={TrendingUp} accent="green" testId="stat-sysprofit" delay={0.25} compact />
        <StatCard label={t("agent_profit_owed")} value={fmtMoney(s.agent_profit_owed ?? 0)} icon={Award} accent="gold" testId="stat-agentprofit" delay={0.3} compact />
        <StatCard label={t("pending_withdrawals")} value={s.pending_withdrawals ?? 0} icon={Wallet} accent="red" testId="stat-pendingw" delay={0.35} compact />
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-heading font-semibold">{t("activity_7d")}</h2>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={s.chart || []}>
              <defs>
                <linearGradient id="colorAmt" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.6}/>
                  <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Area type="monotone" dataKey="amount" stroke="hsl(var(--chart-1))" strokeWidth={2} fill="url(#colorAmt)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </motion.div>
    </div>
  );
}
